// Step 1 — normalisation of a Case into the derived per-party / per-child facts the engine works with.
// Notes: CAXeng.md §1.1 (+0x04 / +0x68 / exemption auto-calc), §2.7 (50% rule), caxdemo_ui.md §1.1 (labels).
import {
  UNIT_MONTHS, PARTY_LABELS, childNcpFraction, toIrsStatus, otherParty,
  type Case, type Child, type FilingStatus, type IrsStatus, type PartyIndex, type PartyInputs, type PartyLabelSet,
} from '@divorceos/schema';
import { MAX_CHILDREN, TANF_CS_IN_FORMULA_NET } from './constants.js';

export const EXACT_HALF_EPS = 1e-5;

export interface NExemptions {
  personal: number; special: number; newSpouse: number; dependent: number;
  ctc: number; eic: number; ccc: number; under6: number; otherDependents: number;
}

export interface NChild {
  index: number;
  name: string;
  dateOfBirth?: string;
  custodialParent: PartyIndex;
  /** Fraction of time (0..1) with the NON-custodial parent. */
  ncpFraction: number;
  /** Age at the end of the tax year (null when no DOB). */
  age: number | null;
  exactlyHalf: boolean;
  userCs?: number;
  userCsPayor?: PartyIndex;
}

export interface NParty {
  index: PartyIndex;
  in: PartyInputs;
  status: FilingStatus;
  irs: IrsStatus;
  /** IRS status used for the CA return (domestic partners may file jointly for CA only). */
  stateIrs: IrsStatus;
  exemptions: NExemptions;
  /** 65+/blind counts on this party's return (party, plus new spouse when MFJOUT; MFJIN adds the other party in the tax pass). */
  age65: number;
  blind: number;
  /** n[p]: children for whom this party is the custodial parent (case+0x04). */
  n: number;
  /** ts[p]: this party's average time fraction with the OTHER party's children (case+0x68). */
  ts: number;
  /** Average fraction of all children's time spent with this party (Timeshare_percentage/All_kids). */
  timeshareAllKids: number;
  /** Max fraction of time any child of this relationship spends with this party (CA JCHH). */
  maxChildTimeshare: number;
  /** Row 8 "income used for child support only" (+ TANF/CS received when TANF_CS_IN_FORMULA_NET). */
  csOnlyIncome: number;
  /** Row 25 allocable add-on items: this relationship + visits/school/travel + uninsured health. */
  addOnItems: number;
  /** Row 23 entered hardship items other than the computed FC 4071(b) line. */
  hardshipItems: number;
}

export interface NCase {
  c: Case;
  year: number;
  parties: [NParty, NParty];
  children: NChild[];
  nkids: number;
  labelSet: PartyLabelSet;
  labels: [string, string];
  errors: string[];
  warnings: string[];
  prorateJoint: boolean;
}

const yearOf = (iso?: string): number | null => {
  if (!iso) return null;
  const y = Number(iso.slice(0, 4));
  return Number.isFinite(y) && y > 1800 ? y : null;
};

/** Age reached by 31 December of `year` (birth-year arithmetic, as the original: "born after Y-17" etc.). */
export function ageAtYearEnd(dateOfBirth: string | undefined, year: number): number | null {
  const by = yearOf(dateOfBirth);
  return by === null ? null : year - by;
}

/** §1.2 (0x1e98 block): 65+ when born before February of (Y − 64). */
export function isAge65(dateOfBirth: string | undefined, year: number): boolean {
  if (!dateOfBirth) return false;
  const y = yearOf(dateOfBirth);
  if (y === null) return false;
  const m = Number(dateOfBirth.slice(5, 7)) || 1;
  return y < year - 64 || (y === year - 64 && m < 2);
}

/** Children in the engine's order: oldest first (the original keeps its child records sorted by date of birth; the per-child allocation removes them in that order), children without a date of birth last, ties in input order. */
export function sortChildrenOldestFirst<T extends { dateOfBirth?: string }>(children: T[]): T[] {
  return children.map((ch, i) => ({ ch, i })).sort((a, b) => {
    const da = a.ch.dateOfBirth ?? '', db = b.ch.dateOfBirth ?? '';
    if (da && db && da !== db) return da < db ? -1 : 1;
    if (da && !db) return -1;
    if (!da && db) return 1;
    return a.i - b.i;
  }).map((x) => x.ch);
}

export function normalizeChildren(c: Case): NChild[] {
  return sortChildrenOldestFirst(c.children.slice(0, MAX_CHILDREN)).map((ch: Child, index) => {
    const f = childNcpFraction(ch, UNIT_MONTHS);
    return {
      index, name: ch.name, dateOfBirth: ch.dateOfBirth, custodialParent: ch.custodialParent,
      ncpFraction: f, age: ageAtYearEnd(ch.dateOfBirth, c.taxYear), exactlyHalf: Math.abs(f - 0.5) < EXACT_HALF_EPS,
      userCs: ch.userCs, userCsPayor: ch.userCsPayor,
    };
  });
}

/**
 * Auto exemption tally (FUN_10015785 / FUN_100159d2): dependents = children with CP == p whose timeshare is not
 * exactly 50% (a child at exactly 50 % is still counted as the CP's qualifying child for the CTC / EIC / CCC /
 * under-6 counts — verified against the original); CTC < 17, EIC < 19, CCC < 13, under 6 for the CA YCTC;
 * personal 1; new spouse 1 when MFJOUT; "other supported children" (case+0x08) are added to the
 * dependent/CTC/EIC/CCC counts (UNCERTAIN in the notes). A child without a DOB is treated as a qualifying child
 * (< 17) but not as under 6.
 */
export function autoExemptions(p: PartyInputs, index: PartyIndex, children: NChild[], year: number): NExemptions {
  const ex: NExemptions = { personal: 1, special: 0, newSpouse: 0, dependent: 0, ctc: 0, eic: 0, ccc: 0, under6: 0, otherDependents: 0 };
  for (const ch of children) {
    if (ch.custodialParent !== index) continue;
    if (!ch.exactlyHalf) ex.dependent += 1;
    const age = ch.age ?? 0;
    if (age < 17) ex.ctc += 1; else ex.otherDependents += 1;
    if (age < 19) ex.eic += 1;
    if (age < 13) ex.ccc += 1;
    if (ch.age !== null && age < 6) ex.under6 += 1;
  }
  const other = Math.max(0, Math.round(p.hardship.otherSupportedChildren));
  ex.dependent += other; ex.ctc += other; ex.eic += other; ex.ccc += other;
  if (p.filingStatus === 'MFJOUT') ex.newSpouse = 1;
  ex.special = (isAge65(p.taxInfo.dateOfBirth, year) ? 1 : 0) + (p.taxInfo.blind ? 1 : 0);
  if (p.filingStatus === 'MFJOUT') ex.special += (isAge65(p.taxInfo.newSpouseDateOfBirth, year) ? 1 : 0) + (p.taxInfo.newSpouseBlind ? 1 : 0);
  return ex;
}

function enteredExemptions(p: PartyInputs): NExemptions {
  const e = p.exemptions;
  return {
    personal: e.personal, special: e.special, newSpouse: e.newSpouse, dependent: e.dependent,
    ctc: e.qualifyingChildrenCtc, eic: e.qualifyingChildrenEic, ccc: e.qualifyingChildrenCcc, under6: e.childrenUnder6,
    otherDependents: e.otherDependents,
  };
}

export function partyLabelSet(c: Case, nkids: number): PartyLabelSet {
  if (c.settings.domesticPartnership) return 'PARTNER_AB';
  if (c.settings.sameSexMarriage) return 'PARTY_AB';
  if (nkids > 0) return 'FATHER_MOTHER';
  return 'HUSBAND_WIFE';
}

/** Step 1 of the engine: derived facts, validation errors and warnings. */
export function normalizeCase(c: Case): NCase {
  const errors: string[] = [];
  const warnings: string[] = [];
  const year = c.taxYear;
  const children = normalizeChildren(c);
  if (c.children.length > MAX_CHILDREN) errors.push('No more than 10 children allowed.');
  if (children.some((ch) => ch.exactlyHalf)) {
    warnings.push('You have entered exactly 50% custodial time for one or more children. This will generally produce significant undesirable tax consequences.');
  }

  // Effective filing status validation (FUN_10009018): the "-in" statuses must be mutual.
  const [s0, s1] = [c.parties[0].filingStatus, c.parties[1].filingStatus];
  if ((s0 === 'MFJIN') !== (s1 === 'MFJIN') || (s0 === 'MFSIN') !== (s1 === 'MFSIN')) errors.push('Incompatible filing statuses');
  if (s0 === 'MFJIN' && c.settings.taxState[0] !== c.settings.taxState[1]) errors.push('Tax states must be the same for MFJIN.');

  const parties = [0, 1].map((i) => {
    const index = i as PartyIndex;
    const p = c.parties[index];
    const o = otherParty(index);
    // Per-party child counts / NCP time (case+0x04 / +0x68): derived from the children when any exist.
    let n: number, ts: number, allKids: number, maxTs: number;
    if (children.length > 0) {
      const mine = children.filter((ch) => ch.custodialParent === index);
      const theirs = children.filter((ch) => ch.custodialParent === o);
      n = mine.length;
      ts = theirs.length ? theirs.reduce((a, ch) => a + ch.ncpFraction, 0) / theirs.length : 0;
      const withMe = (ch: NChild) => (ch.custodialParent === index ? 1 - ch.ncpFraction : ch.ncpFraction);
      allKids = children.reduce((a, ch) => a + withMe(ch), 0) / children.length;
      maxTs = children.reduce((a, ch) => Math.max(a, withMe(ch)), 0);
    } else {
      n = Math.max(0, Math.round(p.childrenAsCustodial));
      ts = Math.max(0, Math.min(1, p.timeWithNcpPercent / 100));
      const nOther = Math.max(0, Math.round(c.parties[o].childrenAsCustodial));
      const otherTs = Math.max(0, Math.min(1, c.parties[o].timeWithNcpPercent / 100));
      const total = n + nOther;
      allKids = total > 0 ? (n * (1 - otherTs) + nOther * ts) / total : 0;
      maxTs = Math.max(n > 0 ? 1 - otherTs : 0, nOther > 0 ? ts : 0);
    }
    const exemptions = p.exemptions.auto ? autoExemptions(p, index, children, year) : enteredExemptions(p);
    const age65 = (isAge65(p.taxInfo.dateOfBirth, year) ? 1 : 0) + (p.filingStatus === 'MFJOUT' && isAge65(p.taxInfo.newSpouseDateOfBirth, year) ? 1 : 0);
    const blind = (p.taxInfo.blind ? 1 : 0) + (p.filingStatus === 'MFJOUT' && p.taxInfo.newSpouseBlind ? 1 : 0);
    const csOnlyIncome = p.nonTaxable.forChildSupportOnly + (TANF_CS_IN_FORMULA_NET ? p.tanfReceived + p.childSupportReceived : 0);
    const np: NParty = {
      index, in: p, status: p.filingStatus, irs: toIrsStatus(p.filingStatus), stateIrs: toIrsStatus(p.stateFilingStatus ?? p.filingStatus),
      exemptions, age65, blind, n, ts, timeshareAllKids: allKids, maxChildTimeshare: maxTs, csOnlyIncome,
      addOnItems: p.childCare.thisRelationship + p.childCare.visitsSchoolTravel + p.childCare.uninsuredHealth,
      hardshipItems: p.hardship.extraordinaryHealth + p.hardship.catastrophicLosses,
    };
    return np;
  }) as [NParty, NParty];

  const nkids = parties[0].n + parties[1].n;
  const labelSet = partyLabelSet(c, nkids);
  return {
    c, year, parties, children, nkids, labelSet, labels: [...PARTY_LABELS[labelSet]] as [string, string], errors, warnings,
    prorateJoint: s0 === 'MFJIN' && s1 === 'MFJIN',
  };
}
